import { MultiSelect, Stack } from '@mantine/core'
import { Suspense, useEffect, useRef } from 'react'
import { z } from 'zod'
import { IconChartBar } from '@tabler/icons-react'
import RowProgress from '../../../../stories/RowProgress/RowProgress'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { Fmt, useFmtColor } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import { useResolvedContext } from './useResolvedContext'
import {
  ColorSchemaFields,
  ComponentStatisticFields,
  ContextFields,
  ContextSchemaFields,
  NEUTRAL_COLOR,
  NeutralColorsToggle,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'

const MAX_KPIS = 6

const Schema = z.object({
  ...ContextSchemaFields,
  ...ColorSchemaFields,
  title: z.string().default('Total KPI Performance'),
  metric_keys: z.array(z.string()).default([]),
  component: z.string().default('percent'),
  statistic: z.string().default('value'),
})

type Settings = z.infer<typeof Schema>

function pickMetrics(metrics: Metric[], settings: Settings): Metric[] {
  if (settings.metric_keys.length > 0) {
    const byKey = new Map(metrics.map((m) => [m.key, m]))
    return settings.metric_keys
      .map((k) => byKey.get(k))
      .filter((m): m is Metric => !!m)
      .slice(0, MAX_KPIS)
  }
  return metrics.filter((m) => m.is_kpi).slice(0, MAX_KPIS)
}

function FormattedRow({
  metricKey,
  title,
  percentValue,
  actualValue,
  displayComponent,
  statistic,
  neutral,
}: {
  metricKey: string
  title: string
  percentValue: number
  actualValue: number | null
  displayComponent: string
  statistic: string
  neutral: boolean
}) {
  const thresholdColor = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic,
    value: percentValue,
  })
  const color = neutral ? NEUTRAL_COLOR : thresholdColor
  const valueLabel =
    displayComponent === 'actual' ? (
      <Fmt
        metric={metricKey}
        component="actual"
        statistic={statistic}
        value={actualValue}
      />
    ) : (
      <Fmt
        metric={metricKey}
        component="percent"
        statistic={statistic}
        value={percentValue}
      />
    )
  return (
    <RowProgress
      title={title}
      value={percentValue}
      color={color}
      valueLabel={valueLabel}
    />
  )
}

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const target = pickMetrics(allMetrics, settings)
  const ctx = useResolvedContext(settings)
  const metricKeys = target.map((m) => m.key)

  const { data: percentPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: metricKeys,
    components: metricKeys.map(() => 'percent'),
    statistics: metricKeys.map(() => settings.statistic),
    competition_id: ctx.competition_id,
  })

  const { data: labelPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: metricKeys,
    components: metricKeys.map(() => settings.component),
    statistics: metricKeys.map(() => settings.statistic),
    competition_id: ctx.competition_id,
  })

  if (target.length === 0) {
    return <WidgetEmpty message="No metrics" />
  }

  const percentByMetric = new Map<number, number>()
  for (const point of percentPoints) {
    percentByMetric.set(point.metric, point.value)
  }
  const actualByMetric = new Map<number, number>()
  for (const point of labelPoints) {
    actualByMetric.set(point.metric, point.value)
  }

  const rows = target
    .map((m) => ({
      metricKey: m.key,
      title: m.short_name || m.name,
      percentValue: percentByMetric.get(m.metric_id) ?? null,
      actualValue: actualByMetric.get(m.metric_id) ?? null,
    }))
    // Keep genuine 0% rows; drop only metrics with no percent data point
    // (no target / no data) so they show nothing rather than a fake 0%.
    .filter(
      (r): r is typeof r & { percentValue: number } => r.percentValue !== null
    )

  if (rows.length === 0) {
    return <WidgetEmpty message="No data" />
  }

  return (
    <Stack gap="sm" h="100%" style={{ overflow: 'auto' }}>
      {rows.map((r) => (
        <FormattedRow
          key={r.metricKey}
          metricKey={r.metricKey}
          title={r.title}
          percentValue={r.percentValue}
          actualValue={r.actualValue}
          displayComponent={settings.component}
          statistic={settings.statistic}
          neutral={settings.use_neutral_colors}
        />
      ))}
    </Stack>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSection title={settings.title}>
      <WidgetSuspense>
        <Inner settings={settings} />
      </WidgetSuspense>
    </WidgetSection>
  )
}

function KpiMultiSelectInner({
  value,
  onChange,
}: {
  value: string[]
  onChange: (val: string[]) => void
}) {
  const { data: metrics } = useMetrics()
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (hydratedRef.current) return
    if (value.length > 0) {
      hydratedRef.current = true
      return
    }
    if (metrics.length === 0) return
    const firstSix = metrics
      .filter((m) => m.is_kpi)
      .slice(0, MAX_KPIS)
      .map((m) => m.key)
    hydratedRef.current = true
    if (firstSix.length > 0) onChange(firstSix)
  }, [metrics, value, onChange])

  const data = metrics.map((m) => ({ value: m.key, label: m.name || m.key }))

  return (
    <MultiSelect
      label={`KPIs (max ${MAX_KPIS})`}
      placeholder={
        value.length === 0 ? `Pick up to ${MAX_KPIS} metrics` : undefined
      }
      data={data}
      value={value}
      onChange={(next) => onChange(next.slice(0, MAX_KPIS))}
      maxValues={MAX_KPIS}
      searchable
      clearable
      nothingFoundMessage="No metrics"
    />
  )
}

function KpiMultiSelect(props: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  return (
    <Suspense
      fallback={
        <MultiSelect
          label={`KPIs (max ${MAX_KPIS})`}
          data={[]}
          disabled
          placeholder="Loading..."
        />
      }
    >
      <KpiMultiSelectInner {...props} />
    </Suspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <KpiMultiSelect
        value={value.metric_keys}
        onChange={(v) => patch({ metric_keys: v })}
      />
      <ComponentStatisticFields
        component={value.component}
        statistic={value.statistic}
        compatibility={KpiRowProgressListWidget.compatibility}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <ContextFields
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        period_type={value.period_type}
        period={value.period}
        use_max_period={value.use_max_period}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiRowProgressListWidget: DashboardWidget<Settings> = {
  type: 'kpi_row_progress_list',
  label: 'KPI Progress List',
  description: 'Row progress bars per KPI metric',
  icon: IconChartBar,
  minW: 2,
  minH: 3,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: {
    percent: ['value', 'value_ytd'],
    actual: ['value', 'value_ytd'],
  },
  Component,
  OptionsForm,
}
